import React, { useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Auth.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);

  const { signIn, error: authError, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const from = location.state?.from?.pathname || '/dashboard';

  // Enhanced navigation function with DOMException handling and exponential backoff
  const safeNavigate = useCallback((path: string, options?: any, retryCount = 0) => {
    if (!isNavigating) {
      setIsNavigating(true);
      try {
        navigate(path, options);
        // Reset navigation lock after successful navigation
        setTimeout(() => setIsNavigating(false), 500);
      } catch (error: any) {
        console.error('Navigation error:', error);
        
        // Handle DOMException specifically
        if (error instanceof DOMException || error.name === 'DOMException') {
          if (error.message.includes('Location or History APIs') && retryCount < 3) {
            console.warn(`Navigation rate limited, retrying in ${Math.pow(2, retryCount) * 500}ms...`);
            
            // Exponential backoff retry
            setTimeout(() => {
              setIsNavigating(false);
              safeNavigate(path, options, retryCount + 1);
            }, Math.pow(2, retryCount) * 500);
            return;
          } else if (error.message.includes('insecure')) {
            console.warn('Insecure navigation operation detected, falling back to location.href');
            // Fallback to direct navigation
            setTimeout(() => {
              window.location.href = path;
            }, 100);
            return;
          }
        }
        
        // Set local error for display
        setLocalError('Navigation failed. Please try refreshing the page.');
        setTimeout(() => setIsNavigating(false), 500);
      }
    } else if (retryCount === 0) {
      // If navigation is already in progress and this is the first attempt, wait and retry
      setTimeout(() => {
        safeNavigate(path, options, retryCount + 1);
      }, 100);
    }
  }, [navigate, isNavigating]);

  // Consolidated effect to handle both success messages and authentication redirect
  React.useEffect(() => {
    // Clear any existing timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    // Handle success message first (without navigation)
    if (location.state?.message && !successMessage) {
      setSuccessMessage(location.state.message);
      // Clear the message from location state using history API directly
      const newState = { ...location.state };
      delete newState.message;
      window.history.replaceState(newState, '', location.pathname);
    }

    // Handle authentication redirect with debounce
    if (isAuthenticated && !isNavigating) {
      navigationTimeoutRef.current = setTimeout(() => {
        safeNavigate(from, { replace: true });
      }, 100); // Small delay to prevent rapid navigation
    }

    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, [isAuthenticated, location.state?.message, successMessage, from, safeNavigate, isNavigating]);

  // Clear errors when component mounts or form fields change
  React.useEffect(() => {
    clearError();
    setLocalError('');
    // Don't clear success message here to avoid conflicts
  }, [email, password, clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!email.trim()) {
      setLocalError('Email is required');
      return;
    }
    
    if (!password.trim()) {
      setLocalError('Password is required');
      return;
    }

    setIsLoading(true);
    setLocalError('');
    clearError();

    try {
      await signIn(email.trim(), password);
      // Navigation will happen automatically via useEffect when isAuthenticated changes
    } catch (error: any) {
      // Error is handled by AuthContext, but we can set a local error if needed
      if (!authError) {
        setLocalError(error.message || 'Failed to sign in');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <span className="logo-icon">🎒</span>
            <h1>Simple Packing</h1>
          </div>
          <h2>Sign In</h2>
          <p>Welcome back! Please sign in to your account.</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}
          
          {(authError || localError) && (
            <div className="error-message">
              {authError?.message || localError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/signup" className="auth-link">
              Sign up
            </Link>
          </p>
          <Link to="/forgot-password" className="auth-link">
            Forgot your password?
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
